import type { FC } from "react";
import { useEffect, useState } from "react";
import { proxy } from "valtio";
import { GeoJSON, Geometry, Point } from "geojson";
import {
  CircleLayerSpecification,
  LineLayerSpecification,
  LngLatBoundsLike,
  LngLatLike,
  Map as MapLib,
  MapMouseEvent,
} from "maplibre-gl";
import { FillLayerSpecification } from "@maplibre/maplibre-gl-style-spec";
import { bbox } from "@turf/bbox";
import { ExternalLink, X } from "lucide-react";
import { Link } from "@renewmap/ui/link";
import { Checkbox } from "@renewmap/ui/checkbox";
import { Label } from "@renewmap/ui/label";
import { Button } from "@renewmap/ui/button";
import { ColumnDiff, EntityTable } from "@renewmap/model";

let map: MapLib | null = null;

/**
 * Idempotent function to render the geometry on the map
 */
const upsertSourceData = (map: MapLib, id: string, data: GeoJSON | null) => {
  const source = map.getSource(id);
  if (source) {
    // @ts-expect-error https://maplibre.org/maplibre-gl-js/docs/examples/live-update-feature/
    source.setData(data);
  } else {
    map.addSource(id, {
      type: "geojson",
      data: data || {
        type: "FeatureCollection",
        features: [],
      },
    });
  }
};

const updateSources = (map: MapLib, selectedRow: RowWithGeom) => {
  upsertSourceData(map, "before-point", selectedRow?.point.public as GeoJSON);
  upsertSourceData(map, "before", selectedRow?.geom_area.public as GeoJSON);
  upsertSourceData(
    map,
    "before-line-geom",
    selectedRow?.line.public as GeoJSON,
  );
  upsertSourceData(map, "after-point", selectedRow?.point.draft as GeoJSON);
  upsertSourceData(map, "after", selectedRow?.geom_area.draft as GeoJSON);
  upsertSourceData(map, "after-line-geom", selectedRow?.line.draft as GeoJSON);
};

const addChangelogPoint = (map: MapLib, point: Point | null | undefined) => {
  const layer = "changelog-point";
  const source = "changelog";

  if (point) {
    // show the update
    upsertSourceData(map, source, point as GeoJSON);
    !map.getLayer(layer) &&
      map.addLayer({
        type: "circle",
        source,
        id: layer,
        paint: {
          "circle-color": "#7EFBCD",
          "circle-opacity": 1,
          "circle-radius": 5,
        },
      } as CircleLayerSpecification);
  } else {
    // remove the update layer as it's been cleared
    if (map.getLayer(layer)) {
      map.removeLayer(layer);
      map.removeSource(source);
    }
  }
};

/**
 * Render and fit to bounds the current draft and public geometry
 */
const addGeomLayers = (map: MapLib, selectedRow: RowWithGeom) => {
  if (selectedRow?.point.public) {
    upsertSourceData(map, "before-point", selectedRow?.point.public as GeoJSON);

    map.addLayer({
      type: "circle",
      source: "before-point",
      id: "before-point",
      paint: {
        "circle-color": "red",
        "circle-opacity": 0.4,
        "circle-radius": 5,
        "circle-stroke-width": 1,
        "circle-stroke-color": "black",
      },
    } as CircleLayerSpecification);

    map.addLayer({
      type: "symbol",
      source: "before-point",
      id: "before-point-text",
      layout: {
        "text-field": rowLabel(selectedRow, "public") ?? undefined,
        "text-variable-anchor": ["bottom"],
        "text-radial-offset": 0.5,
        "text-justify": "auto",
        "icon-allow-overlap": true,
      },
      paint: {
        "text-color": "hsl(15, 0%, 100%)",
        "text-halo-blur": 1,
        "text-halo-color": "hsl(15, 0%, 0%)",
        "text-halo-width": 1,
      },
      minzoom: 8,
    });
  }

  if (selectedRow?.geom_area.public) {
    upsertSourceData(map, "before", selectedRow?.geom_area.public as GeoJSON);

    map.addLayer({
      type: "fill",
      source: "before",
      id: "before-fill",
      paint: {
        "fill-color": "red",
        "fill-opacity": 0.4,
      },
    } as FillLayerSpecification);
    map.addLayer({
      type: "line",
      source: "before",
      id: "before-line",
      paint: {
        "line-color": "red",
        "line-opacity": 1,
      },
    } as LineLayerSpecification);
  }

  if (selectedRow?.line.public) {
    upsertSourceData(
      map,
      "before-line-geom",
      selectedRow?.line.public as GeoJSON,
    );

    map.addLayer({
      type: "line",
      source: "before-line-geom",
      id: "before-line-geom",
      paint: {
        "line-color": "red",
        "line-opacity": 1,
        "line-width": 2,
      },
    } as LineLayerSpecification);
  }

  if (selectedRow?.point.draft) {
    upsertSourceData(map, "after-point", selectedRow?.point.draft as GeoJSON);
    map.addLayer({
      type: "circle",
      source: "after-point",
      id: "after-point",
      paint: {
        "circle-color": "cyan",
        "circle-opacity": 0.3,
        "circle-radius": 5,
        "circle-stroke-width": 1,
        "circle-stroke-color": "black",
      },
    } as CircleLayerSpecification);

    map.addLayer({
      type: "symbol",
      source: "after-point",
      id: "after-point-text",
      layout: {
        "text-field": rowLabel(selectedRow, "draft") ?? undefined,
        "text-variable-anchor": ["bottom"],
        "text-radial-offset": 0.5,
        "text-justify": "auto",
        "icon-allow-overlap": true,
      },
      paint: {
        "text-color": "hsl(15, 0%, 100%)",
        "text-halo-blur": 1,
        "text-halo-color": "hsl(15, 0%, 0%)",
        "text-halo-width": 1,
      },
      minzoom: 8,
    });
  }

  if (selectedRow?.geom_area.draft) {
    upsertSourceData(map, "after", selectedRow?.geom_area.draft as GeoJSON);
    map.addLayer({
      type: "fill",
      source: "after",
      id: "after-fill",
      paint: {
        "fill-color": "cyan",
        "fill-opacity": 0.3,
      },
    } as FillLayerSpecification);
    map.addLayer({
      type: "line",
      source: "after",
      id: "after-line",
      paint: {
        "line-color": "cyan",
        "line-opacity": 0.7,
      },
    } as LineLayerSpecification);
  }

  if (selectedRow?.line.draft) {
    upsertSourceData(
      map,
      "after-line-geom",
      selectedRow?.line.draft as GeoJSON,
    );

    map.addLayer({
      type: "line",
      source: "after-line-geom",
      id: "after-line-geom",
      paint: {
        "line-color": "cyan",
        "line-opacity": 0.7,
        "line-width": 2,
      },
    } as LineLayerSpecification);
  }
};

const fitToGeomArea = (map: MapLib, selectedRow: RowWithGeom) => {
  // If we have a changelog point and zoom, use those instead
  if (selectedRowState.changelogPoint && selectedRowState.changelogZoom) {
    map.flyTo({
      center: selectedRowState.changelogPoint.coordinates as LngLatLike,
      zoom: selectedRowState.changelogZoom,
      duration: 0,
    });
    return;
  }

  // Otherwise, fly to the bounds of the current geometry
  const someGeom =
    selectedRow?.geom_area.draft ||
    selectedRow?.geom_area.public ||
    selectedRow?.line.draft ||
    selectedRow?.line.public;

  if (someGeom) {
    const bounds = bbox(someGeom as Geometry);
    map.fitBounds(bounds as LngLatBoundsLike, {
      duration: 0,
    });
    return;
  }

  const somePoint = (selectedRow?.point.draft ||
    selectedRow?.point.public) as Point;
  if (somePoint) {
    map.flyTo({
      center: somePoint.coordinates as LngLatLike,
      zoom: 14,
      duration: 0,
    });
  }
};

interface GeomColumnDiff {
  draft: GeoJSON | null;
  public: GeoJSON | null;
}

// infrastructure_name excluded: label renders on the point column, which infrastructure_v5 doesn't have
export interface RowWithGeom {
  project_name: ColumnDiff<string> | undefined;
  substation_name: ColumnDiff<string> | undefined;
  id: string;
  geom_area: GeomColumnDiff;
  point: GeomColumnDiff;
  line: GeomColumnDiff;
}

const rowLabel = (
  row: RowWithGeom,
  column: "draft" | "public",
): string | null | undefined =>
  row.project_name?.[column] ?? row.substation_name?.[column];

const emptyGeomColumnDiff: GeomColumnDiff = { draft: null, public: null };

// fill all geometries with empty geometry if they are null, so that the map can render them
export const toRowWithGeom = (row: {
  id: string;
  values: Record<string, unknown>;
}): RowWithGeom =>
  ({
    geom_area: emptyGeomColumnDiff,
    point: emptyGeomColumnDiff,
    line: emptyGeomColumnDiff,
    ...row.values,
    id: row.id,
  }) as RowWithGeom;

export const selectedRowState = proxy({
  //TODO: rename to selectedProjectRow to avoid confusion
  selectedRow: null as null | RowWithGeom,
  type: null as null | EntityTable | "changelog",
  changelogPoint: null as Point | null,
  changelogZoom: null as number | null,
});

export interface GeomInfoEditPanelProps {
  selectedRow: RowWithGeom | null;
  changelogPoint: Point | null | undefined;
  setChangelogPoint?: (point: Point | null, zoom: number | null) => void;
  onReady?: VoidFunction;
}

export const GeomInfoEditPanel: FC<GeomInfoEditPanelProps> = ({
  selectedRow,
  changelogPoint,
  setChangelogPoint,
  onReady,
}) => {
  const [mapContainer, setMapContainer] = useState<HTMLDivElement | null>(null);
  const [showDraft, setShowDraft] = useState(true);
  const [showPublic, setShowPublic] = useState(true);

  const onClick = (point: Point | null, zoom: number | null) => {
    if (selectedRowState.type === "changelog") {
      addChangelogPoint(map!, point);
      setChangelogPoint?.(point, zoom);
    }
  };

  useEffect(() => {
    if (!mapContainer) {
      return;
    }

    if (!map) {
      // only create the map once
      map = new MapLib({
        container: mapContainer,
        style:
          "https://api.maptiler.com/maps/682061e4-1113-4fc6-84ab-3601fdc28fd9/style.json?key=2hdyuWT7ZZSdwGPbS7PP",
        center: [137.08627556506167, -26.655776020113002], // Center AUS
        maplibreLogo: false,
        attributionControl: false,
      });

      if (onClick) {
        map!.on("mouseup", (e: MapMouseEvent) => {
          const point: Point = {
            type: "Point",
            coordinates: [e.lngLat.lng, e.lngLat.lat],
          };
          onClick(point, Math.round(map!.getZoom()));
        });

        map!.on("dblclick", () => {
          // double click to clear the changelog location
          addChangelogPoint(map!, null);
          onClick(null, null);
        });
      }

      map.once("styledata", () => {
        if (selectedRow !== null) {
          addGeomLayers(map!, selectedRow as RowWithGeom);
          addChangelogPoint(map!, changelogPoint);
          fitToGeomArea(map!, selectedRow as RowWithGeom);
          onReady?.();
        }
      });
    } else if (selectedRow !== null) {
      updateSources(map!, selectedRow as RowWithGeom);
      addChangelogPoint(map!, changelogPoint);
      fitToGeomArea(map!, selectedRow as RowWithGeom);
    }

    return () => {
      map?.remove();
      map = null;
    };
  }, [mapContainer, selectedRow, changelogPoint]);

  if (!selectedRow) {
    return null;
  }

  const controls = (
    <div className="flex gap-3">
      <div className="mb-1 flex items-center space-x-2">
        <Checkbox
          id="draft"
          disabled={
            !selectedRow.geom_area.draft &&
            !selectedRow.point.draft &&
            !selectedRow.line.draft
          }
          checked={showDraft}
          onCheckedChange={(checked) => {
            setShowDraft(Boolean(checked));
            const visible = checked ? "visible" : "none";
            map?.setLayoutProperty("after-fill", "visibility", visible);
            map?.setLayoutProperty("after-line", "visibility", visible);
            map?.setLayoutProperty("after-point", "visibility", visible);
            map?.setLayoutProperty("after-point-text", "visibility", visible);
            map?.setLayoutProperty("after-line-geom", "visibility", visible);
          }}
        />
        <Label htmlFor="draft" className="flex-grow cursor-pointer font-normal">
          Draft
        </Label>
      </div>
      <div className="mb-1 flex items-center space-x-2">
        <Checkbox
          id="public"
          disabled={
            !selectedRow.geom_area.public &&
            !selectedRow.point.public &&
            !selectedRow.line.public
          }
          checked={showPublic}
          onCheckedChange={(checked) => {
            setShowPublic(Boolean(checked));
            const visible = checked ? "visible" : "none";
            map?.setLayoutProperty("before-fill", "visibility", visible);
            map?.setLayoutProperty("before-line", "visibility", visible);
            map?.setLayoutProperty("before-point", "visibility", visible);
            map?.setLayoutProperty("before-point-text", "visibility", visible);
            map?.setLayoutProperty("before-line-geom", "visibility", visible);
          }}
        />
        <Label
          htmlFor="public"
          className="flex-grow cursor-pointer font-normal"
        >
          Public
        </Label>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="ml-auto h-6 w-6"
        onClick={() => {
          selectedRowState.selectedRow = null;
        }}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );

  if (
    !selectedRow.geom_area.public &&
    !selectedRow.geom_area.draft &&
    !selectedRow.point.public &&
    !selectedRow.point.draft &&
    !selectedRow.line.public &&
    !selectedRow.line.draft
  ) {
    // render an empty gray square
    return (
      <div className="max-w-[400px] overflow-x-hidden rounded-xl bg-white p-4">
        {controls}
        <div className="flex h-[300px] w-[300px] overflow-hidden rounded-2xl bg-gray-400">
          <div className="m-auto">No geometry data</div>
        </div>
      </div>
    );
  }

  const somePoint = (selectedRow?.point.draft ||
    selectedRow?.point.public) as Point;
  const geomHashParam = somePoint
    ? `#14/${somePoint.coordinates[1]}/${somePoint.coordinates[0]}`
    : null;

  return (
    <div className="flex max-w-[400px] flex-col gap-1 overflow-x-hidden rounded-xl bg-white p-4">
      {controls}
      <div
        id="map-container"
        ref={setMapContainer}
        className="h-[300px] w-[300px] overflow-hidden rounded-2xl"
      />
      {geomHashParam ? (
        <Link
          href={`https://map.renewmap.com.au/${geomHashParam}`}
          target="_blank"
          rel="noreferrer"
          className="mx-auto flex gap-2"
        >
          <ExternalLink className="m-auto" size={18} />
          Open in RenewMap
        </Link>
      ) : null}
    </div>
  );
};
